import { AddIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormControl,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SkeletonCircle,
  SkeletonText,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import AppTableWithPageMeta from 'components/AppTableWithPageMeta';
import DeleteConfirmationModal from 'components/genericForms/DeleteConfirmationModal';
import currencySymbolMap from 'currency-symbol-map';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiEye } from 'react-icons/fi';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useDeleteAdMutation,
  useDuplicateAdMutation,
  useGetAdComparisonQuery,
  useGetAdsInsightsQuery,
  useGetAdsListQuery,
  useUpdateAdStatusMutation,
} from 'store/campaigns/adsApi';
import {
  selectAdsForInsights,
  selectAfter,
  selectBefore,
  selectCurrentPage,
  selectSelectedAdsetId,
  selectSelectedAdsId,
  selectSelectedCampaignId,
  setAdsForInsights,
  setAfter,
  setBefore,
  setCurrentPage,
} from 'store/campaigns/campaignsSlice';
import SingleImageAdUpdateForm from './Forms/AdForms/UpdationForms/SingleImageAdUpdateForm';
import SingleVideoAdUpdateForm from './Forms/AdForms/UpdationForms/SingleVideoAdUpdateForm';
import CarouselCatalogueAdUpdateForm from './Forms/AdForms/UpdationForms/CarouselCatalogueAdUpdateForm';
import CarouselAdUpdateForm from './Forms/AdForms/UpdationForms/CarouselAdUpdateForm';
import SizeAdUpdateForm from './Forms/AdForms/UpdationForms/SizeAdUpdateForm';
import SingleCatalogueAdUpdateForm from './Forms/AdForms/UpdationForms/SingleCatalogueAdUpdateForm';
import CreateAdForm from './Forms/AdForms/CreationForms/SingleForms/CreateAdForm';

const formatScore = (score) => {
  if (score === 0) return '-';
  return `${score.toFixed(2)}%`;
};

function AdsTable({ adsAccountData, startDate, endDate, datePreset, onRefresh, onRefreshComplete }) {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const prevAccountIdRef = useRef(adsAccountData?.account_id);
  const urlInitDoneRef = useRef(false);
  const selectedCampaignId = useSelector(selectSelectedCampaignId);
  const selectedAdsetId = useSelector(selectSelectedAdsetId);
  const selectedAdsId = useSelector(selectSelectedAdsId);
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const AdsForInsights = useSelector(selectAdsForInsights);
  const after = useSelector(selectAfter);
  const before = useSelector(selectBefore);
  const currentPage = useSelector(selectCurrentPage);
  const currencyCode = adsAccountData?.currency;
  const currencySymbol = currencySymbolMap(adsAccountData?.currency);

  const { isOpen: isOpenComparison, onOpen: onOpenComparison, onClose: onCloseComparison } = useDisclosure();
  const isSearchDisabled = Boolean(selectedAdsId);
  const isAdsetSelected = Boolean(selectedAdsetId);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDuplicateModalOpen,
    onOpen: onDuplicateModalOpen,
    onClose: onDuplicateModalClose,
  } = useDisclosure();

  const isMounted = useRef(false);
  const [isCreateFormVisible, setIsCreateFormVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedAd, setSelectedAd] = useState(null);
  const [switchStates, setSwitchStates] = useState({});
  const [adsListWithInsights, setAdsListWithInsights] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  // Local loader to flip ON immediately on account change
  const [isLoadingAdsWithInsights, setIsLoadingAdsWithInsights] = useState(true);

  const [copyCount, setCopyCount] = useState(1);
  const [fetchedComparison, setfetchedComparison] = useState(null);

  const {
    data: adsList,
    isFetching: isAdsListFetching,
    isUninitialized: isAdsListUninitialized,
    isLoading: isAdsListLoading,
    refetch: refetchAdsList,
    error: adsListError,
  } = useGetAdsListQuery(
    {
      pageSize: '20',
      ...(searchValue ? {} : { after, before }),
      searchValue,
      adsAccountId: adsAccountData?.account_id,
      adset_ids: selectedAdsetId
        ? [selectedAdsetId?.adset_id ? selectedAdsetId?.adset_id : selectedAdsetId]
        : [],
      campaign_ids: selectedCampaignId
        ? [selectedCampaignId?.campaign_id ? selectedCampaignId?.campaign_id : selectedCampaignId]
        : [],
      ad_ids: selectedAdsId ? [selectedAdsId] : [],
    },
    { skip: !adsAccountData?.account_id }
  );

  const {
    data: adsInsights,
    isUninitialized: isAdsInsightsUninitialized,
    refetch: refetchAdsInsights,
    isLoading: isAdsInsightsLoading,
    error: AdsInsightsError,
  } = useGetAdsInsightsQuery(
    {
      searchValue,
      adsAccountId: adsAccountData?.account_id,
      ...(datePreset ? {} : { start_date: startDate, end_date: endDate }),
      date_preset: datePreset,
      ads: AdsForInsights?.length > 0 ? AdsForInsights : [],
    },
    { skip: !adsAccountData?.account_id || AdsForInsights?.length === 0 }
  );

  const shouldFetchComparison = selectedAd?.hashes && selectedAd?.hashes.length > 0;

  const {
    data: adsComparison,
    isLoading: isComparisonLoading,
    isFetching: isComparisonFetching,
    refetch: comparisonRefetch,
  } = useGetAdComparisonQuery(
    {
      hashes: selectedAd?.hashes ? selectedAd?.hashes : '',
    },
    { skip: !selectedAd || !shouldFetchComparison || !isOpenComparison }
  );

  const [updateAdStatus] = useUpdateAdStatusMutation({});
  const [deleteAd] = useDeleteAdMutation();
  const [duplicateAd, { isLoading: isAdDuplicating }] = useDuplicateAdMutation();

  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);

  useEffect(() => {
    if (adsComparison?.length > 0) setfetchedComparison(adsComparison);
  }, [adsComparison]);

  useEffect(() => {
    if (selectedAd?.hashes && selectedAd?.hashes.length > 0 && isOpenComparison) {
      comparisonRefetch();
    }
  }, [selectedAd, isOpenComparison, comparisonRefetch]);

  useEffect(() => {
    const prevId = prevAccountIdRef.current;
    const currId = adsAccountData?.account_id;

    if (prevId && currId && prevId !== currId) {
        setIsLoadingAdsWithInsights(true);

        // clear local derived state to avoid stale rows
        setSelectedAd(null);
        setSwitchStates({});
        setAdsListWithInsights([]);
        setSearchValue('');
        setfetchedComparison(null);

        // clear paging so new account starts at page 0
        dispatch(setAfter(''));
        dispatch(setBefore(''));
        dispatch(setCurrentPage(0));

        // clear insights input so query hook re-derives fresh list
        dispatch(setAdsForInsights([]));

        // refetch list (insights will refetch once inputs are set)
        refetchAdsList();
    }
    prevAccountIdRef.current = currId;
  }, [adsAccountData?.account_id]);

  // Optional external refresh trigger — also show loader immediately
  useEffect(() => {
    if (onRefresh) {
      setIsLoadingAdsWithInsights(true);
      refetchAdsList();
      onRefreshComplete && onRefreshComplete();
    }
  }, [onRefresh, refetchAdsList, onRefreshComplete]);

  // Build switch states & prep insight query list
  useEffect(() => {
    if (adsList?.data) {
      const initialSwitchStates = {};
      adsList.data.forEach((ad) => {
        initialSwitchStates[ad?.id] = ad.status;
      });
      setSwitchStates(initialSwitchStates);

      const formattedAds = adsList.data.reduce((acc, ad) => {
        const promotedObject = ad?.adset?.promoted_object;

        const base = {
          id: ad?.id,
          destination_type: ad?.adset?.destination_type,
          optimization_goal: ad?.adset?.optimization_goal,
        };

        const full = promotedObject ? { ...base, promoted_object: promotedObject } : base;
        if (ad?.cpa) full.cpa = ad?.cpa;

        acc.push(full);
        return acc;
      }, []);

      dispatch(setAdsForInsights(formattedAds));
    }
  }, [adsList, dispatch]);

  // Stitch list + insights; drop loader after slight debounce to prevent flicker
  useEffect(() => {
    if (adsList?.data || adsInsights) {
      const stitched = (adsList?.data || []).map((ad) => {
        const insightData = adsInsights?.find((i) => i?.ad_id === ad?.id);
        return { ...ad, insight: insightData?.insights || {} };
      });

      setAdsListWithInsights(stitched);

      const t = setTimeout(() => setIsLoadingAdsWithInsights(false), 100);
      return () => clearTimeout(t);
    }
  }, [adsList, adsInsights]);

  // ---------------- Handlers ----------------
  const handleSwitchChange = (adId, currentState) => {
    const newStatus = currentState === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setSwitchStates((prev) => ({ ...prev, [adId]: newStatus }));

    updateAdStatus({
      adId,
      payload: {
        status: newStatus,
        adsAccount: {
          id: adsAccountData?.id,
          adsAccountId: adsAccountData?.account_id,
          adsAccountName: adsAccountData?.name,
          businessName: adsAccountData?.business?.name,
          businessId: adsAccountData?.business?.id,
        },
      },
    })
      .unwrap()
      .then(() => {
        if (isMounted.current) {
          refetchAdsList();
          toast({
            position: 'top-right',
            title: `Ad ${newStatus === 'ACTIVE' ? 'activated' : 'paused'}`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        if (isMounted.current) {
          toast({
            position: 'top-right',
            title: `Error updating Ad status`,
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
        console.error('Error updating Ad status:', error);
        setSwitchStates((prev) => ({ ...prev, [adId]: currentState }));
      });
  };

  const handleDuplicateAd = () => {
    duplicateAd({ adId: selectedAd?.id, payload: { copy_count: copyCount } })
      .unwrap()
      .then(() => {
        refetchAdsList();
        toast({
          position: 'top-right',
          title: 'Ad duplicated sucessfully',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          position: 'top-right',
          title: 'Error duplicating ad',
          status: 'error',
          description: error?.data?.message,
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => {
        setSelectedAd(null);
        onDuplicateModalClose();
      });
  };

  const handlePageChange = ({ after, before, currentPage }) => {
    if (after) {
      dispatch(setAfter(after));
      dispatch(setBefore(''));
    } else if (before) {
      dispatch(setBefore(before));
      dispatch(setAfter(''));
    }
    dispatch(setCurrentPage(currentPage));
  };

  // 1) On mount, read URL once and seed Redux
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stored = params.get('page_id');
    const parsed = stored !== null ? Number(stored) : NaN;

    if (Number.isInteger(parsed) && parsed >= 0) {
      dispatch(setCurrentPage(parsed));
    } else {
      // write page_id=0 into URL so both stay in sync from the start
      const next = new URLSearchParams(location.search);
      next.set('page_id', '0');
      navigate({ search: next.toString() }, { replace: true });
    }
    urlInitDoneRef.current = true;
  }, []);

  // 2) Whenever currentPage changes, push it to the URL (no readback loop)
  useEffect(() => {
    if (!urlInitDoneRef.current) return;

    const params = new URLSearchParams(location.search);
    const existing = params.get('page_id');
    if (existing === String(currentPage)) return; // already in sync

    params.set('page_id', String(currentPage));
    navigate({ search: params.toString() }, { replace: true });
    // only depend on currentPage & location.search so we re-evaluate when URL actually changes
  }, [currentPage, location.search]);

  const handleDeleteAd = (adData) => {
    deleteAd({ adId: adData?.id, adsAccountId: adsAccountData?.account_id })
      .unwrap()
      .then(() => {
        refetchAdsList();
        toast({
          position: 'top-right',
          title: 'Ad deleted',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((error) => {
        toast({
          position: 'top-right',
          title: 'Error deleting ad',
          status: 'error',
          description: error?.data?.message,
          duration: 4000,
          isClosable: true,
        });
      });
  };

  // ---------------- Columns ----------------
  const columns = useMemo(
    () => [
      {
        Header: 'Off/On',
        accessor: '',
        Filter: '',
        filter: '',
        width: 120,
        Cell: ({ row: { original } }) => {
          const isActive = switchStates[original?.id] ?? original.status === 'ACTIVE';
          const tooltipText = isActive ? 'Ad is ON' : 'Ad is OFF';

          return (
            <Flex gap={'10px'} alignItems={'center'} justifyContent={'center'}>
              <Tooltip label={tooltipText} fontSize="xs">
                <FormControl width={'fit-content'}>
                  <Switch
                    colorScheme="brand"
                    size={'sm'}
                    isChecked={original.status === 'ACTIVE'}
                    onChange={() => handleSwitchChange(original?.id, original.status)}
                    isDisabled={userInfo?.role !== 'client'}
                  />
                </FormControl>
              </Tooltip>
            </Flex>
          );
        },
      },
      {
        Header: 'Name',
        accessor: 'name',
        Filter: '',
        filter: '',
        width: 300,
        Cell: ({ row: { original } }) => {
          const isSelected = selectedAdsId === original?.id;

          return (
            <Box display={'flex'} dir="row" gap={2} alignItems={'center'}>
              <Flex>
                {original?.imageUrl ? (
                  <img
                    src={original?.imageUrl}
                    alt={`${original?.name} thumbnail`}
                    style={{
                      width: '45px',
                      height: '45px',
                      minWidth: '45px',
                      border: '0.5px solid grey',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <img
                    src={original?.imageUrl}
                    alt={' '}
                    style={{
                      width: '45px',
                      height: '45px',
                      minWidth: '45px',
                      border: '0.5px solid grey',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </Flex>
              <Flex direction={'column'}>
                <Box>
                  <Flex gap={'10px'} alignItems={'center'}>
                    <Text
                      fontWeight={isSelected ? 'bold' : 'normal'}
                      color={isSelected ? 'brand.500' : 'inherit'}
                      fontSize={'14px'}
                    >
                      {original.name}
                    </Text>
                  </Flex>
                </Box>
                <Box display={'flex'} dir="row" gap={2}>
                  <Flex
                    alignItems={'center'}
                    gap={0.5}
                    onClick={() => {
                      if (userInfo?.role === 'client') {
                        setSelectedAd(original);
                        onOpen();
                      }
                    }}
                  >
                    <Icon
                      as={MdEdit}
                      cursor={userInfo?.role === 'client' ? 'pointer' : 'not-allowed'}
                      color={userInfo?.role === 'client' ? 'inherit' : 'gray.300'}
                      fontSize={'12px'}
                    />
                    <Link to={'#'}>
                      <Text
                        _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                        fontWeight={'normal'}
                        color={'inherit'}
                        fontSize={'11px'}
                      >
                        Edit
                      </Text>
                    </Link>
                  </Flex>
                  <Flex
                    alignItems={'center'}
                    gap={0.5}
                    onClick={() => {
                      if (userInfo?.role === 'client') {
                        setSelectedAd(original);
                        onDuplicateModalOpen();
                      }
                    }}
                  >
                    <Icon
                      as={HiOutlineDocumentDuplicate}
                      cursor={userInfo?.role === 'client' ? 'pointer' : 'not-allowed'}
                      color={userInfo?.role === 'client' ? 'inherit' : 'gray.300'}
                      fontSize={'12px'}
                    />
                    <Link to={'#'}>
                      <Text
                        _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                        fontWeight={'normal'}
                        color={'inherit'}
                        fontSize={'11px'}
                      >
                        Duplicate
                      </Text>
                    </Link>
                  </Flex>
                </Box>
              </Flex>
            </Box>
          );
        },
      },
      {
        Header: 'Delivery',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => {
          const getStatusMessage = (status) => {
            switch (status) {
              case 'WITH_ISSUES':
                return 'Delivery error';
              case 'CAMPAIGN_PAUSED':
                return 'Campaign Off';
              case 'ADSET_PAUSED':
                return 'Adset Off';
              case 'PAUSED':
                return 'Off';
              case 'IN_PROCESS':
                return 'Processing';
              case 'PENDING_REVIEW':
                return 'In review';
              default:
                return status || 'Unknown';
            }
          };

          const errorMessage = original?.issues_info?.[0]?.error_message || '';

          const getCircleStyles = (status) => {
            switch (status) {
              case 'WITH_ISSUES':
                return { bg: 'rgb(161,11,24)', border: 'none' };
              case 'CAMPAIGN_PAUSED':
              case 'ADSET_PAUSED':
                return { bg: 'rgb(216,221,225)', border: 'none' };
              case 'ACTIVE':
              case 'IN_PROCESS':
              case 'PENDING_REVIEW':
                return { bg: 'rgb(255,255,255)', border: '2px solid green' };
              default:
                return { bg: 'rgb(255,255,255)', border: 'none' };
            }
          };

          const circleStyles = getCircleStyles(original?.effective_status);

          const formatTooltipMessage = (message) =>
            message.split('\n').map((part, index) => (
              <span key={index}>
                {part}
                <br />
              </span>
            ));

          const getTooltipMessage = (status) => {
            switch (status) {
              case 'WITH_ISSUES':
                return errorMessage;
              case 'PAUSED':
                return 'This ad is turned off.';
              case 'ACTIVE':
                return 'This campaign is on and running normally.';
              case 'ADSET_PAUSED':
                return 'This ad is on, but its ad set is turned off.';
              case 'CAMPAIGN_PAUSED':
                return 'This ad set is on, but its campaign is turned off.';
              case 'IN_PROCESS':
                return 'Your ad is processing.\nMeta is processing your recent updates to your ad. Some updates may take several hours.';
              default:
                return '';
            }
          };

          const tooltipMessage = getTooltipMessage(original?.effective_status);

          return (
            <Flex>
              {original?.effective_status === 'WITH_ISSUES' ? (
                <Flex alignItems={'center'}>
                  <Box
                    bg={circleStyles.bg}
                    border={circleStyles.border}
                    borderRadius="50%"
                    boxSize="0.8rem"
                    display="inline-block"
                    mr={2}
                  />
                  <Tooltip label={formatTooltipMessage(tooltipMessage)} aria-label="Error message" hasArrow p={2} fontSize={'12px'}>
                    <Text fontSize={'14px'}>{getStatusMessage(original?.effective_status)}</Text>
                  </Tooltip>
                </Flex>
              ) : (
                <Flex alignItems={'center'}>
                  {original?.effective_status === 'PAUSED' || original?.effective_status === 'ARCHIVED' ? null : (
                    <Box
                      bg={circleStyles.bg}
                      border={circleStyles.border}
                      borderRadius="50%"
                      boxSize="0.8rem"
                      display="inline-block"
                      mr={2}
                    />
                  )}
                  {original?.effective_status !== 'PENDING_REVIEW' && (
                    <Tooltip label={formatTooltipMessage(tooltipMessage)} aria-label="Error message" hasArrow p={2} fontSize={'12px'}>
                      <Text fontSize={'14px'}>{getStatusMessage(original?.effective_status)}</Text>
                    </Tooltip>
                  )}
                  {original?.effective_status === 'PENDING_REVIEW' && (
                    <Text fontSize={'14px'}>{getStatusMessage(original?.effective_status)}</Text>
                  )}
                </Flex>
              )}
            </Flex>
          );
        },
      },
      {
        Header: 'Comparison',
        accessor: 'Comparison',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex align={'center'} justifyContent={'center'}>
            <Tooltip label="View Comparison" fontSize="xs">
              <Flex>
                <Icon
                  as={FiEye}
                  cursor="pointer"
                  onClick={() => {
                    setSelectedAd(original);
                    onOpenComparison();
                  }}
                  _hover={{ color: 'gray.500' }}
                />
              </Flex>
            </Tooltip>
          </Flex>
        ),
      },
      {
        Header: 'Budget',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => {
          const { daily_budget, lifetime_budget } = original?.adset || {};
          const displayLifetimeBudget = Number(lifetime_budget) > 0 ? (Number(lifetime_budget) / 100).toFixed(2) : null;
          const displayDailyBudget = Number(daily_budget) > 0 ? (Number(daily_budget) / 100).toFixed(2) : null;

          const isLifetimeBudget = displayLifetimeBudget !== null;
          const displayBudget = isLifetimeBudget
            ? `${currencySymbol}${displayLifetimeBudget}`
            : displayDailyBudget !== null
            ? `${currencySymbol}${displayDailyBudget}`
            : 'No Budget';
          const budgetType = isLifetimeBudget ? 'Lifetime' : displayDailyBudget !== null ? 'Daily' : '';

          return (
            <Flex direction={'column'}>
              <Text fontSize={'14px'}>{displayBudget}</Text>
              {budgetType && <Text fontSize={'10px'} color={'gray.500'}>{budgetType}</Text>}
            </Flex>
          );
        },
      },
      {
        Header: 'Spend',
        accessor: 'spend',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Text fontSize={'14px'}>
            {original.insight?.spend ? `${currencySymbol}${Number(original.insight.spend).toFixed(2)}` : '-'}
          </Text>
        ),
      },
      {
        Header: 'Breakeven CPA',
        accessor: 'cpa',
        Filter: '',
        filter: '',
        width: 180,
        Cell: ({ row: { original } }) => (
          <Text fontSize={'14px'} fontWeight={'bold'}>
            {original.cpa ? `${currencySymbol}${Number(original.cpa).toFixed(2)}` : '-'}
          </Text>
        ),
      },
      {
        Header: 'Cost per result',
        accessor: '',
        Filter: '',
        filter: '',
        width: 180,
        Cell: ({ row: { original } }) => (
          <Box>
            <Text fontSize={'14px'} fontWeight={'bold'}>
              {original.insight?.cpr ? `${currencySymbol}${Number(original.insight.cpr).toFixed(2)}` : '-'}
            </Text>
          </Box>
        ),
      },
      {
        Header: 'Scoring',
        accessor: 'score',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => {
          const score = original.insight?.score || 0;
          return (
            <Text fontSize={'14px'} fontWeight={'bold'}>
              {formatScore(score)}
            </Text>
          );
        },
      },
      {
        Header: 'Results',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => {
          const { insight = {}, adset = {} } = original;

          const eventNameMap = {
            destination_type: {
              ON_AD: 'Meta Leads',
              App: 'In-app subscribes',
            },
            optimization_goal: {
              APP_INSTALLS: 'Mobile App Installation',
              LEAD_GENERATION: 'Leads',
              THRUPLAY: 'Thruplay',
              LINK_CLICKS: 'Link Clicks',
              PAGE_LIKES: 'Follows or Likes',
              QUALITY_CALL: '20-second Call',
              REACH: 'Reach',
              IMPRESSIONS: 'Reach',
            },
          };

          let derivedEventName = null;
          if (eventNameMap.destination_type[adset.destination_type]) {
            derivedEventName = eventNameMap.destination_type[adset.destination_type];
          } else if (eventNameMap.optimization_goal[adset.optimization_goal]) {
            derivedEventName = eventNameMap.optimization_goal[adset.optimization_goal];
          }

          const originalEventName = insight?.eventName ? insight.eventName.charAt(0).toUpperCase() + insight.eventName.slice(1) : null;

          return (
            <Flex direction="column" mb={-4}>
              <Text fontSize="14px">{insight?.results ? insight.results : '-'}</Text>
              {(derivedEventName || originalEventName) && (
                <Text fontSize="10px" color="gray.500">
                  {derivedEventName || originalEventName}
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        Header: 'CPC',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>
              {original.insight?.cpc ? `${currencySymbol}${Number(original.insight?.cpc).toFixed(2)}` : '-'}
            </Text>
          </Flex>
        ),
      },
      {
        Header: 'CTR',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>
              {original.insight?.ctr ? `${Number(original.insight?.ctr).toFixed(2)}%` : '-'}
            </Text>
          </Flex>
        ),
      },
      {
        Header: 'CPM',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.cpm ? Number(original.insight?.cpm).toFixed(2) : '-'}</Text>
          </Flex>
        ),
      },
      {
        Header: 'Clicks',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.clicks ? original.insight?.clicks : '-'}</Text>
          </Flex>
        ),
      },
      {
        Header: 'Purchase Value',
        accessor: '',
        Filter: '',
        filter: '',
        width: 180,
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>
              {original.insight?.purchaseValue && original.insight.purchaseValue !== '-'
                ? `${currencySymbol}${parseFloat(original.insight.purchaseValue).toFixed(2)}`
                : original.insight?.purchaseValue || '-'}
            </Text>
          </Flex>
        ),
      },
      {
        Header: 'Roas',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => {
          const roas = original.insight?.roas;
          const formattedROAS = roas && !isNaN(parseFloat(roas)) ? parseFloat(roas).toFixed(2) : '-';
          return (
            <Flex>
              <Text fontSize={'14px'}>{formattedROAS}</Text>
            </Flex>
          );
        },
      },
      {
        Header: 'hook Rate',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.hookRate ? `${Number(original.insight?.hookRate).toFixed(2)}%` : '-'}</Text>
          </Flex>
        ),
      },
      {
        Header: 'hold Rate',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'}>
            <Text fontSize={'14px'}>{original.insight?.holdRate ? `${Number(original.insight?.holdRate).toFixed(2)}%` : '-'}</Text>
          </Flex>
        ),
      },
      {
        Header: 'Status',
        accessor: 'status',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => <Text fontSize={'14px'}>{original.status}</Text>,
      },
      {
        Header: 'Created Date',
        accessor: 'createdDate',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Text fontSize={'14px'}>{moment(original.created_time).format('DD-MM-YYYY hh:mm A')}</Text>
        ),
      },
      {
        Header: 'Updated Date',
        accessor: 'updatedDate',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Text fontSize={'14px'}>{moment(original.updated_time).format('DD-MM-YYYY hh:mm A')}</Text>
        ),
      },
      {
        Header: 'Actions',
        accessor: '',
        Filter: '',
        filter: '',
        Cell: ({ row: { original } }) => (
          <Flex gap={'10px'} alignItems={'center'}>
            <Tooltip label="Delete" fontSize="xs">
              <Flex>
                <Icon
                  _hover={{ color: 'gray.400' }}
                  as={MdDelete}
                  cursor={userInfo?.role === 'client' ? 'pointer' : 'not-allowed'}
                  color={userInfo?.role === 'client' ? 'red.500' : 'gray.300'}
                  onClick={() => {
                    if (userInfo?.role === 'client') {
                      setSelectedAd(original);
                      setIsConfirmationModalOpen(true);
                    }
                  }}
                />
              </Flex>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [selectedAdsId, adsAccountData, switchStates, userInfo?.role]
  );

  const confirmDelete = () => {
    if (selectedAd) {
      handleDeleteAd(selectedAd);
      setIsConfirmationModalOpen(false);
      setSelectedAd(null);
    }
  };

  const handleAdUpdateSuccess = () => {
    refetchAdsList();
    dispatch(setAfter(''));
    dispatch(setBefore(''));
    dispatch(setCurrentPage(0));
    localStorage.removeItem('after');
    localStorage.removeItem('before');
    localStorage.removeItem('currentPage');
  };

  const handleOnSuccess = () => {
    if (!isAdsListUninitialized) {
      refetchAdsList();
    }
    if (!isAdsInsightsUninitialized) {
      refetchAdsInsights();
    }
  };

  return (
    <>
      <Flex mb={2} alignItems="center">
        <Tooltip
          p={2}
          label="To create an Ad, please select an Ad set from the Ad sets tab."
          isDisabled={isAdsetSelected}
          placement="right"
          borderRadius={'sm'}
          fontSize={'12px'}
        >
          <Button
            leftIcon={<AddIcon boxSize={3} />}
            colorScheme={isAdsetSelected ? 'brand' : 'gray'}
            size="sm"
            onClick={() => {
              if (isAdsetSelected) setIsCreateFormVisible(true);
            }}
            _hover={{ cursor: isAdsetSelected ? 'pointer' : 'not-allowed' }}
            _disabled={{ bg: 'gray.300', cursor: 'not-allowed' }}
          >
            Create
          </Button>
        </Tooltip>
      </Flex>

      {isCreateFormVisible && (
        <CreateAdForm
          adsAccountData={adsAccountData}
          isOpen={isCreateFormVisible}
          onClose={() => setIsCreateFormVisible(false)}
          onSuccess={handleOnSuccess}
        />
      )}

      <DeleteConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this Ad? This action can't be undone."
      />

      {/* DUPLICATE AD MODAL */}
      <Modal size={'xl'} isCentered={true} isOpen={isDuplicateModalOpen} scrollBehavior={'inside'} onClose={onDuplicateModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize={'md'}>Duplicate your ads</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex gap={1} direction={'column'}>
              <Text fontSize={'14px'} fontWeight={'bold'}>
                Number of copies
              </Text>
              <NumberInput value={copyCount} onChange={(valueString, valueNumber) => setCopyCount(valueNumber)} min={1} size="sm" width={'530px'}>
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper border={'none'} />
                  <NumberDecrementStepper border={'none'} />
                </NumberInputStepper>
              </NumberInput>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" variant="solid" size={'sm'} mr={3} onClick={onDuplicateModalClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" isLoading={isAdDuplicating} size={'sm'} onClick={handleDuplicateAd}>
              Duplicate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Comparison Modal */}
      <Modal
        size={'6xl'}
        isCentered={true}
        isOpen={isOpenComparison}
        onClose={() => {
          onCloseComparison();
          setSelectedAd(null);
          setfetchedComparison(null);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Ad Comparison</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxHeight="500px" overflowY="auto">
            {isComparisonLoading || isComparisonFetching ? (
              <Flex justifyContent={'center'} alignItems={'center'} gap={5} h={'10vh'}>
                <SkeletonCircle size="10" />
                <SkeletonText height="5" width="80%" mb={4} />
              </Flex>
            ) : fetchedComparison && fetchedComparison?.length > 0 ? (
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>OFF/ON</Th>
                    <Th>Ad Account</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Campaign Name</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Ad Name</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Spend</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Breakeven CPA</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Cost per result</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Scoring</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Results</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>CPC</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>CTR</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>CPM</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Clicks</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Purchase Value</Th>
                    <Th sx={{ whiteSpace: 'nowrap' }}>Roas</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {fetchedComparison.map((ad, index) => {
                    const adStatus = adsList?.data.find((adItem) => adItem?.id === ad?.ad?.ad_id)?.status;

                    return (
                      <Tr key={index}>
                        <Td>
                          <Flex gap={'10px'} align={'center'}>
                            <Tooltip label={adStatus === 'ACTIVE' ? 'Ad is ON' : 'Ad is OFF'} fontSize="xs">
                              <FormControl width={'fit-content'}>
                                <Switch
                                  colorScheme="brand"
                                  size={'sm'}
                                  isChecked={adStatus === 'ACTIVE'}
                                  onChange={() => handleSwitchChange(ad?.ad?.ad_id, adStatus)}
                                  isDisabled={userInfo?.role !== 'client'}
                                />
                              </FormControl>
                            </Tooltip>
                          </Flex>
                        </Td>
                        <Td>
                          <Box>
                            <Text fontSize={'14px'} sx={{ whiteSpace: 'nowrap' }}>
                              {ad?.adsAccount?.adsAccountName || 'N/A'}
                            </Text>
                            <Text fontSize={'12px'} color={'gray.500'} sx={{ whiteSpace: 'nowrap' }}>
                              {ad?.adsAccount?.adsAccountId || 'N/A'}
                            </Text>
                          </Box>
                        </Td>
                        <Td>
                          <Text fontSize={'14px'} sx={{ whiteSpace: 'nowrap' }}>
                            {ad?.campaign?.name || 'N/A'}
                          </Text>
                        </Td>
                        <Td>
                          <Flex direction={'row'} alignItems={'center'}>
                            <img
                              src={ad?.creative?.thumbnail_url}
                              alt={`${ad?.ad?.name}`}
                              style={{
                                width: '45px',
                                minWidth: '45px',
                                height: '30px',
                                objectFit: 'cover',
                                border: '0.5px solid grey',
                                marginRight: '10px',
                              }}
                            />
                            <Text fontSize={'14px'} sx={{ whiteSpace: 'nowrap' }}>
                              {ad?.ad?.name || 'N/A'}
                            </Text>
                          </Flex>
                        </Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>
                          {ad?.spend ? `${currencySymbol}${Number(ad?.spend).toFixed(2)}` : '-'}
                        </Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '14px' }}>
                          {ad?.cpa ? Number(ad?.cpa).toFixed(2) : '-'}
                        </Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '14px' }}>
                          {ad?.cpr ? `${currencySymbol}${Number(ad?.cpr).toFixed(2)}` : '-'}
                        </Td>
                        <Td>
                          <Text sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '14px' }}>
                            {formatScore(ad?.score || 0)}
                          </Text>
                        </Td>
                        <Td>
                          <Box>
                            <Text sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>{ad?.results ? ad?.results : '-'}</Text>
                            <Text fontSize={'12px'} sx={{ whiteSpace: 'nowrap' }}>
                              {ad?.eventName ? ad?.eventName : null}
                            </Text>
                          </Box>
                        </Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>
                          {ad?.cpc ? `${currencySymbol}${Number(ad.cpc).toFixed(2)}` : '-'}
                        </Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>
                          {ad?.ctr ? `${Number(ad?.ctr).toFixed(2)}%` : '-'}
                        </Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>{ad?.cpm ? Number(ad?.cpm).toFixed(2) : '-'}</Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>{ad?.clicks ? Number(ad?.clicks).toFixed(2) : '-'}</Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>
                          {ad?.purchaseValue ? `${currencySymbol}${Number(ad?.purchaseValue).toFixed(2)}` : '-'}
                        </Td>
                        <Td sx={{ whiteSpace: 'nowrap', fontSize: '14px' }}>{ad?.roas ? Number(ad?.roas).toFixed(2) : '-'}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            ) : (
              <Flex justifyContent={'center'} alignItems={'center'} gap={5} h={'10vh'}>
                <Text>No comparison data available.</Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* EDIT AD MODAL */}
      <Modal
        size={'6xl'}
        isCentered={true}
        isOpen={isOpen}
        scrollBehavior={'inside'}
        onClose={onClose}
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Ad</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {(() => {
              const storySpec = selectedAd && selectedAd?.adcreative?.object_story_spec;

              if (selectedAd?.adset?.promoted_object?.product_set_id?.length && selectedAd?.adcreative?.asset_feed_spec?.ad_formats?.includes('CAROUSEL')) {
                return (
                  <CarouselCatalogueAdUpdateForm
                    adData={selectedAd}
                    adsAccountData={adsAccountData}
                    onSuccess={handleAdUpdateSuccess}
                    onClose={onClose}
                  />
                );
              } else if (selectedAd?.adset?.promoted_object?.product_set_id?.length && !selectedAd?.adcreative?.asset_feed_spec) {
                return (
                  <SingleCatalogueAdUpdateForm
                    adData={selectedAd}
                    adsAccountData={adsAccountData}
                    onSuccess={handleAdUpdateSuccess}
                    onClose={onClose}
                  />
                );
              } else if (selectedAd && selectedAd?.adcreative?.asset_feed_spec?.ad_formats) {
                return (
                  <SizeAdUpdateForm
                    adData={selectedAd}
                    adsAccountData={adsAccountData}
                    onSuccess={handleAdUpdateSuccess}
                    onClose={onClose}
                  />
                );
              } else if (storySpec?.link_data?.child_attachments) {
                return (
                  <CarouselAdUpdateForm
                    adData={selectedAd}
                    onSuccess={handleAdUpdateSuccess}
                    adsAccountData={adsAccountData}
                    onClose={onClose}
                  />
                );
              } else if (storySpec?.link_data) {
                return (
                  <SingleImageAdUpdateForm
                    adData={selectedAd}
                    adsAccountData={adsAccountData}
                    onSuccess={handleAdUpdateSuccess}
                    onClose={onClose}
                  />
                );
              } else if (storySpec?.video_data) {
                return (
                  <SingleVideoAdUpdateForm
                    adData={selectedAd}
                    adsAccountData={adsAccountData}
                    onSuccess={handleAdUpdateSuccess}
                    onClose={onClose}
                  />
                );
              } else {
                return <Text>No data available for the selected ad.</Text>;
              }
            })()}
          </ModalBody>
        </ModalContent>
      </Modal>

      {adsListError ? (
        <Flex justifyContent={'center'} alignItems={'center'} height={'50vh'}>
          <Text color="red.500">{adsListError?.data?.message || "Sorry, Ads not available at this moment. Try again later."}</Text>
        </Flex>
      ) : isAdsListLoading || isAdsInsightsLoading || isLoadingAdsWithInsights ? (
        <Flex direction={'column'} gap={2} alignItems={'center'} justifyContent={'center'} h={'50vh'}>
          <Spinner />
          <Text>Loading…</Text>
        </Flex>
      ) : (
        <AppTableWithPageMeta
          key={adsAccountData?.account_id || 'no-account'} //  force remount on account switch
          columns={columns}
          adsAccountId={adsAccountData?.account_id}
          datePreset={datePreset}
          currencyCode={currencyCode}
          data={adsListWithInsights || []}
          searchEnabled={!isSearchDisabled}
          isFetching={isAdsListFetching}
          initialState={{ pageIndex: currentPage }}
          pageMeta={adsList?.meta || {}}
          onPageChange={handlePageChange}
          onSearchInputChange={setSearchValue}
          searchTerm={searchValue}
        />
      )}
    </>
  );
}

export default AdsTable;